import {createSlice} from '@reduxjs/toolkit';
import {contactsApi} from '~/common/fabric/EventGuests/api/endpoints/contacts';
import {guestsApi} from '~/common/fabric/EventGuests/api/endpoints/guests';

export const alphabet = 'a b c d e f g h i j k l m n o p q r s t u v w x y z #'.split(' ');

export const paginatedResponseInit = {
  page: [],
  current_page: '1',
  has_next: false,
  count: 0,
  num_pages: 0,
};

export const eventGuestListResponseInit = {
  ...paginatedResponseInit,
  summary: {
    maybe_count: 0,
    no_count: 0,
    undecided_count: 0,
    yes_count: 0,
  },
};

export const guestListParams = {
  q: '',
  search_by: 'all',
  search_type: 'contains',
  sort_by: '',
  reverse: false,
  per_page: 25,
  page: 1,
};

export const contactsParams = {
  ...guestListParams,
  per_page: 55,
  sort_by: 'name',
  import_search_string: '',
};

const initialState = {
  contacts: paginatedResponseInit,
  sent: paginatedResponseInit,
  guests: paginatedResponseInit,
  events: paginatedResponseInit,
  imports: paginatedResponseInit,
  eventGuestList: eventGuestListResponseInit,
  guestsToBeAdded: [],
  addingGuest: false,
  import_search_string: '',
  eventGuestListParams: {
    source_event_id: null,
    per_page: 55,
    page: 1,
    rsvp: null, // all | yes | no | undecided
    search_param: '',
  },
  eventsParams: {
    page: 1,
    events_per_page: 8,
    search_string: null,
    sort: 'event_date_desc',
    search_param: '',
    past_events_only: false,
  },
  selectedPastEvent: {},
  guestListParams,
  letters: alphabet,
  contactsParams,
  contact_group_id: '',
  ui: {
    adding: [],
    creating: false,
    deleting: [],
    editing: null,
    uploading: false,
    guestOptions: false,
    eviteGuestList: false,
    pasteGuestList: false,
    eviteAddressBook: false,
    googleAddressBook: false,
    newGuestContact: false,
    eventGuestList: false,
    eventsListScroll: false,
    name: '',
    email: '',
    phone: '',
    request: {pending: false, error: false},
    importing: false,
    loadingAddressBook: false,
    isUploading: false,
  },
  showShareableLinkBanner: true,
  eventId: null,
  fabricGreetingCard: false,
  invitationMakerFlow: false,
  hasGiftCard: false,
};

export const selectAddingOrDeleting = (state, props) => {
  const {guests} = state;
  const {adding, deleting, request} = guests.ui;
  const {pending} = request;

  return pending && (adding.includes(props.id) || deleting.includes(props.guest_id));
};

const guestsSlice = createSlice({
  name: 'guests',
  initialState,
  reducers: {
    setFetching: (state, action) => ({
      ...state,
      fetching: action.payload,
    }),
    setGuestListParams: (state, action) => ({
      ...state,
      guestListParams: {...state.guestListParams, ...action.payload},
    }),
    setGuestData: (state, action) => ({
      ...state,
      guests: action.payload.guests || [],
      sent: action.payload.sent || [],
      totalDrafts: action.payload.totalDrafts || 0,
      totalSent: action.payload.totalSent || 0,
    }),
    updateContactGroupId: (state, action) => ({
      ...state,
      contact_group_id: action.payload.contact_group_id,
    }),
    updateGuests: (state, action) => ({...state, ...action.payload}),
    updateGuestsUI: (state, action) => ({
      ...state,
      ui: {...state.ui, ...action.payload},
    }),
    clearGuestListSearchParam: (state) => ({
      ...state,
      guestListParams,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(guestsApi.endpoints.fetchGuests.matchPending, (state) => ({
        ...state,
        fetching: true,
      }))
      .addMatcher(guestsApi.endpoints.fetchGuests.matchFulfilled, (state, {payload, meta}) => ({
        ...state,
        guestListParams: meta.arg.originalArgs?.searchParams,
        fetching: false,
        guests: payload.guests || [],
        sent: payload.sent || [],
        totalDrafts: payload.totalDrafts || 0,
        totalSent: payload.totalSent || 0,
      }))
      .addMatcher(guestsApi.endpoints.fetchGuests.matchRejected, (state) => ({
        ...state,
        fetching: false,
        guests: paginatedResponseInit,
      }))
      .addMatcher(contactsApi.endpoints.fetchContacts.matchFulfilled, (state, {payload}) => ({
        ...state,
        ...payload,
      }));
  },
});

export const {
  setFetching,
  setGuestListParams,
  setGuestData,
  updateContactGroupId,
  updateGuests,
  updateGuestsUI,
  clearGuestListSearchParam,
} = guestsSlice.actions;

export const {reducer} = guestsSlice;
